import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Portal from '../../components/Portal';
import {
  UsersIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  FunnelIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const PASSEES_PAR_PAGE = 10;

const OVERLAY_CLASS =
  'fixed top-0 left-0 z-[55] flex h-[100dvh] w-screen items-center justify-center bg-black/50 p-4';

const api = (path) => `${process.env.REACT_APP_API_URL}${path}`;

function roundEuro(n) {
  return Math.round(Number(n) * 100) / 100;
}

function parseMontant(value) {
  const n = roundEuro(String(value ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

function repartirMontantEncaissement(usagesTries, montantDroit, montantCheque) {
  let reste = roundEuro(montantCheque);
  const lignes = usagesTries.map((u) => {
    const du = roundEuro(u.montant);
    if (!(reste > 0)) {
      return { ...u, encaisse: 0, restant: du, issue: 'non_atteint' };
    }
    if (reste + 0.001 >= du) {
      reste = roundEuro(reste - du);
      return { ...u, encaisse: du, restant: 0, issue: 'paye' };
    }
    const encaisse = reste;
    reste = 0;
    return { ...u, encaisse, restant: roundEuro(du - encaisse), issue: 'partiel' };
  });

  let droitIssue = 'absent';
  if (montantDroit > 0) {
    if (reste + 0.001 >= montantDroit) {
      droitIssue = 'paye';
      reste = roundEuro(reste - montantDroit);
    } else if (reste > 0.001) {
      droitIssue = 'insuffisant';
      reste = 0;
    } else {
      droitIssue = 'non_atteint';
    }
  }

  return {
    lignes,
    droitIssue,
    avoir: reste > 0.001 ? reste : 0,
  };
}

function formatDateUsage(date) {
  if (!date) return 'Date inconnue';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'Date inconnue';
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const LABELS_PRESTATION = {
  depot: 'Dépôt de hausses',
  extraction: 'Extraction des hausses',
  extraction_hausses: 'Extraction des hausses',
  cire: 'Gaufrage de cire',
  conditionnement: 'Conditionnement de miel',
  candi: 'Fabrication de candi',
};

const UNITES_QUANTITE = {
  depot: { un: 'hausse', plusieurs: 'hausses' },
  extraction: { un: 'hausse', plusieurs: 'hausses' },
  extraction_hausses: { un: 'hausse', plusieurs: 'hausses' },
  cire: { un: 'kg', plusieurs: 'kg' },
  conditionnement: { un: 'kg', plusieurs: 'kg' },
  candi: { un: 'sac de 25 kg', plusieurs: 'sacs de 25 kg' },
};

function libellePrestationPassee(prestation) {
  if (!prestation?.type) return 'Prestation';
  const label = LABELS_PRESTATION[prestation.type] || prestation.type;
  if (prestation.quantite == null || Number.isNaN(Number(prestation.quantite))) return label;
  const n = Number(prestation.quantite);
  const unite = UNITES_QUANTITE[prestation.type];
  if (!unite) return `${label} (${n})`;
  return `${label} (${n} ${n === 1 ? unite.un : unite.plusieurs})`;
}

function libelleStatutPassee(r) {
  const labels = {
    reservee: 'Réservée (à confirmer)',
    honoree: 'Réalisée',
    annulee: 'Annulée',
    absente: 'Absent·e',
  };
  return labels[r?.statut] || r?.statut || '';
}

function classeStatutPassee(statut) {
  if (statut === 'reservee') return 'bg-blue-100 text-blue-800';
  if (statut === 'honoree') return 'bg-green-100 text-green-800';
  if (statut === 'annulee') return 'bg-red-50 text-red-700';
  if (statut === 'absente') return 'bg-honey-100 text-honey-900';
  return 'bg-slate-100 text-slate-700';
}

function badgesPaiementPassee(r) {
  const p = r?.paiement || {};
  const methodes = {
    cheque: 'Chèque',
    espece: 'Espèces',
    stripe: 'En ligne',
    solde: 'Avoir',
    virement: 'Virement',
  };
  const badges = [];
  if (p.statut === 'paye' || (p.methode && p.statut !== 'en_attente' && p.statut !== 'dette')) {
    if (p.methode && methodes[p.methode]) {
      badges.push({ label: methodes[p.methode], tone: 'paye' });
    } else if (p.statut === 'paye') {
      badges.push({ label: 'Réglé', tone: 'paye' });
    }
  } else if (p.methode && methodes[p.methode] && p.statut === 'dette') {
    badges.push({ label: methodes[p.methode], tone: 'paye' });
  }
  if (p.statut === 'dette') {
    const total = Math.round(Number(r?.montantTotal || 0) * 100) / 100;
    const prepaye = Math.round(Number(r?.montantPrepaye || 0) * 100) / 100;
    const aUnReste = p.montantRestantDette != null && p.montantRestantDette !== '';
    const restant = aUnReste
      ? Math.round(Number(p.montantRestantDette) * 100) / 100
      : Math.round(Math.max(0, total - prepaye) * 100) / 100;
    const dejaPaye = Math.round(Math.max(0, total - restant) * 100) / 100;
    const partiel = restant > 0 && dejaPaye > 0;
    badges.push({
      label: partiel ? 'Mise en dette · partiellement payé' : 'Mise en dette',
      tone: 'dette',
    });
  }
  return badges;
}

function statusServiceLabel(status) {
  const m = {
    en_attente_paiement: 'Attente paiement',
    en_attente_caution: 'Attente caution',
    actif: 'Actif',
    expire: 'Expiré',
    sans_souscription: 'Sans souscription',
  };
  return m[status] || status;
}

function statusBadgeClass(status) {
  if (status === 'actif') return 'bg-green-100 text-green-800';
  if (status === 'en_attente_caution') return 'bg-honey-100 text-honey-900';
  if (status === 'en_attente_paiement') return 'bg-honey-100 text-honey-800';
  if (status === 'sans_souscription') return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}

const STATUT_FILTER_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'actif', label: 'Actif' },
  { value: 'en_attente_paiement', label: 'Attente paiement' },
  { value: 'en_attente_caution', label: 'Attente caution' },
  { value: 'en_attente_validation', label: 'Attente validation' },
  { value: 'expire', label: 'Expiré' },
  { value: 'sans_souscription', label: 'Sans souscription' },
];

const DROIT_USAGE_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'oui', label: 'Réglé' },
  { value: 'non', label: 'Non réglé' },
];

const CAUTION_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: 'oui', label: 'Déposée' },
  { value: 'non', label: 'Non déposée' },
];

function FilterButton({ label, badge, open, onToggle, children, menuWidth = 'w-56' }) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
      >
        <FunnelIcon className="h-4 w-4 mr-2" />
        {label}
        {badge ? (
          <span className="ml-2 px-2 py-0.5 bg-honey-500 rounded-full text-xs">{badge}</span>
        ) : null}
      </button>
      {open && (
        <div
          className={`absolute z-10 mt-2 ${menuWidth} bg-white rounded-lg shadow-lg border border-slate-200`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function FilterOption({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
        selected ? 'bg-honey-50 text-honey-700' : 'hover:bg-slate-50 text-slate-700'
      }`}
    >
      <span>{children}</span>
      {selected ? <CheckIcon className="h-4 w-4 text-honey-600" /> : null}
    </button>
  );
}

export default function GestionUsagers({ canManageActivite = true }) {
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usagers, setUsagers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterDroitUsage, setFilterDroitUsage] = useState('');
  const [filterCaution, setFilterCaution] = useState('');
  const [openFilter, setOpenFilter] = useState('');
  const [cautionTarget, setCautionTarget] = useState(null);
  const [cautionForm, setCautionForm] = useState({
    dateReception: '',
    note: '',
    document: null,
  });
  const [cautionSubmitting, setCautionSubmitting] = useState(false);
  const [cautionError, setCautionError] = useState('');
  const [encaissementTarget, setEncaissementTarget] = useState(null);
  const [encaissementDetail, setEncaissementDetail] = useState(null);
  const [encaissementLoading, setEncaissementLoading] = useState(false);
  const [encaissementForm, setEncaissementForm] = useState({
    methode: 'cheque',
    reference: '',
    note: '',
  });
  const [usageIdsCoches, setUsageIdsCoches] = useState([]);
  const [droitCoche, setDroitCoche] = useState(false);
  const [montantCheque, setMontantCheque] = useState('');
  const [montantEdite, setMontantEdite] = useState(false);
  const [confirmerAvoir, setConfirmerAvoir] = useState(false);
  const [encaissementSubmitting, setEncaissementSubmitting] = useState(false);
  const [encaissementError, setEncaissementError] = useState('');
  const [passeesTarget, setPasseesTarget] = useState(null);
  const [passeesListe, setPasseesListe] = useState([]);
  const [passeesLoading, setPasseesLoading] = useState(false);
  const [passeesError, setPasseesError] = useState('');
  const [passeesPage, setPasseesPage] = useState(0);
  const passeesListRef = useRef(null);

  const anneesOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  useEffect(() => {
    fetchUsagers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annee]);

  const fetchUsagers = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(api(`/miellerie/usagers?annee=${annee}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsagers(Array.isArray(data?.usagers) ? data.usagers : []);
    } catch (err) {
      setUsagers([]);
      setError(err.response?.data?.message || 'Erreur lors du chargement des usagers');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const openCautionModal = (usager) => {
    setCautionTarget(usager);
    setCautionForm({
      dateReception: new Date().toISOString().slice(0, 10),
      note: '',
      document: null,
    });
    setCautionError('');
  };

  const closeCautionModal = () => {
    if (cautionSubmitting) return;
    setCautionTarget(null);
    setCautionForm({ dateReception: '', note: '', document: null });
    setCautionError('');
  };

  const handleMarkCautionRecu = async () => {
    const serviceId = cautionTarget?.serviceId;
    if (!serviceId) return;
    setCautionError('');
    setCautionSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('status', 'recu');
      if (cautionForm.dateReception) fd.append('dateReception', cautionForm.dateReception);
      if (cautionForm.note?.trim()) fd.append('note', cautionForm.note.trim());
      if (cautionForm.document) fd.append('documentCaution', cautionForm.document);
      await axios.put(api(`/services/${serviceId}/caution`), fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Caution marquée comme reçue');
      setCautionTarget(null);
      setCautionForm({ dateReception: '', note: '', document: null });
      await fetchUsagers({ silent: true });
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de la mise à jour de la caution';
      setCautionError(msg);
      toast.error(msg);
    } finally {
      setCautionSubmitting(false);
    }
  };

  const openEncaissementModal = async (usager) => {
    const userId = usager?.user?._id;
    if (!userId) return;
    setEncaissementTarget(usager);
    setEncaissementDetail(null);
    setUsageIdsCoches([]);
    setDroitCoche(false);
    setMontantCheque('');
    setMontantEdite(false);
    setConfirmerAvoir(false);
    setEncaissementError('');
    setEncaissementForm({
      methode: 'cheque',
      reference: '',
      note: '',
    });
    setEncaissementLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        api(`/miellerie/balance/usager/${userId}?annee=${annee}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const usages = Array.isArray(data?.usages) ? data.usages : [];
      const droit = data?.droitUsage || null;
      setEncaissementDetail(data);
      setUsageIdsCoches(usages.map((u) => String(u._id)));
      setDroitCoche(Boolean(droit));
    } catch (err) {
      setEncaissementError(
        err.response?.data?.message || 'Impossible de charger la dette'
      );
    } finally {
      setEncaissementLoading(false);
    }
  };

  const closeEncaissementModal = () => {
    if (encaissementSubmitting) return;
    setEncaissementTarget(null);
    setEncaissementDetail(null);
    setUsageIdsCoches([]);
    setDroitCoche(false);
    setMontantCheque('');
    setMontantEdite(false);
    setConfirmerAvoir(false);
    setEncaissementError('');
  };

  const openReservationsPassees = async (usager) => {
    const userId = usager?.user?._id;
    if (!userId || !(usager.nbReservationsPassees > 0)) return;
    setPasseesTarget(usager);
    setPasseesListe([]);
    setPasseesPage(0);
    setPasseesError('');
    setPasseesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        api(`/miellerie/usagers/${userId}/reservations-passees?annee=${annee}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasseesListe(Array.isArray(data?.reservations) ? data.reservations : []);
    } catch (err) {
      setPasseesError(
        err.response?.data?.message || 'Impossible de charger les réservations passées'
      );
    } finally {
      setPasseesLoading(false);
    }
  };

  const closeReservationsPassees = () => {
    setPasseesTarget(null);
    setPasseesListe([]);
    setPasseesPage(0);
    setPasseesError('');
  };

  const passeesTotalPages = Math.max(1, Math.ceil(passeesListe.length / PASSEES_PAR_PAGE));
  const passeesPageCourante = Math.min(passeesPage, passeesTotalPages - 1);
  const passeesVisibles = passeesListe.slice(
    passeesPageCourante * PASSEES_PAR_PAGE,
    passeesPageCourante * PASSEES_PAR_PAGE + PASSEES_PAR_PAGE
  );

  useEffect(() => {
    if (passeesListRef.current) passeesListRef.current.scrollTop = 0;
  }, [passeesPageCourante, passeesTarget]);

  const montantEncaissement = useMemo(() => {
    const usages = encaissementDetail?.usages || [];
    const droit = encaissementDetail?.droitUsage;
    const sommeUsages = usages
      .filter((u) => usageIdsCoches.includes(String(u._id)))
      .reduce((s, u) => s + (Number(u.montant) || 0), 0);
    const sommeDroit = droitCoche && droit ? Number(droit.montant) || 0 : 0;
    return roundEuro(sommeUsages + sommeDroit);
  }, [encaissementDetail, usageIdsCoches, droitCoche]);

  const repartition = useMemo(() => {
    const usages = (encaissementDetail?.usages || []).filter((u) =>
      usageIdsCoches.includes(String(u._id))
    );
    const droit = droitCoche ? encaissementDetail?.droitUsage : null;
    const montantDroit = droit ? roundEuro(Number(droit.montant) || 0) : 0;
    const montant = parseMontant(montantCheque);
    if (!(montant > 0)) {
      return { lignes: [], droitIssue: droit ? 'absent' : 'absent', avoir: 0, montantValide: false };
    }
    const resultat = repartirMontantEncaissement(usages, montantDroit, montant);
    if (droit && !(montantDroit > 0)) {
      resultat.droitIssue = 'paye';
    }
    return { ...resultat, montantValide: true };
  }, [encaissementDetail, usageIdsCoches, droitCoche, montantCheque]);

  useEffect(() => {
    if (!encaissementTarget || montantEdite) return;
    setMontantCheque(montantEncaissement > 0 ? montantEncaissement.toFixed(2) : '');
  }, [encaissementTarget, montantEncaissement, montantEdite]);

  useEffect(() => {
    setConfirmerAvoir(false);
  }, [repartition.avoir]);

  const toggleUsageCoche = (usage) => {
    const id = String(usage._id);
    setUsageIdsCoches((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleDroitCoche = () => {
    setDroitCoche((prev) => !prev);
  };

  const handleEncaisserDette = async () => {
    const userId = encaissementTarget?.user?._id;
    if (!userId) return;
    const droit = droitCoche ? encaissementDetail?.droitUsage : null;
    const montant = parseMontant(montantCheque);
    if (!usageIdsCoches.length && !droit?.serviceId) {
      setEncaissementError("Cochez au moins un usage ou le droit d'usage");
      return;
    }
    if (!(montant > 0)) {
      setEncaissementError('Indiquez le montant encaissé');
      return;
    }
    if (repartition.lignes.some((l) => l.issue === 'non_atteint')) {
      setEncaissementError('Le montant ne couvre pas tous les usages cochés. Décochez ceux qui ne sont pas encaissés.');
      return;
    }
    if (droit && (repartition.droitIssue === 'insuffisant' || repartition.droitIssue === 'non_atteint')) {
      setEncaissementError("Le montant ne couvre pas le droit d'usage en entier. Décochez-le, ou augmentez le montant.");
      return;
    }
    if (repartition.avoir > 0 && !confirmerAvoir) {
      setEncaissementError(`Confirmez que le surplus de ${repartition.avoir.toFixed(2)} € est un avoir.`);
      return;
    }
    setEncaissementError('');
    setEncaissementSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        api('/miellerie/balance/encaisser'),
        {
          userId,
          montant,
          confirmerAvoir: repartition.avoir > 0,
          methode: encaissementForm.methode,
          reference: encaissementForm.reference.trim() || undefined,
          note: encaissementForm.note.trim() || undefined,
          reservationIds: usageIdsCoches,
          serviceId: droit?.serviceId || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const mode = encaissementForm.methode === 'cheque' ? 'chèque' : 'espèces';
      if (data.avertissement) {
        toast.warn(data.avertissement);
      }
      const n = Number(data.reservationsMisesAJour) || 0;
      const morceaux = [];
      if (n > 0) {
        morceaux.push(
          `${n} usage${n > 1 ? 's' : ''} marqué${n > 1 ? 's' : ''} réglé${n > 1 ? 's' : ''} par ${mode}`
        );
      }
      const partiels = Number(data.reservationsPartielles) || 0;
      if (partiels > 0) {
        morceaux.push(
          `${partiels} usage${partiels > 1 ? 's' : ''} encaissé${partiels > 1 ? 's' : ''} partiellement`
        );
      }
      if (data.droitUsageRegle) {
        morceaux.push(`droit d'usage marqué réglé par ${mode}`);
      }
      if (Number(data.avoir) > 0) {
        morceaux.push(`avoir de ${Number(data.avoir).toFixed(2)} € crédité`);
      }
      toast.success(
        morceaux.length
          ? `Encaissement enregistré. ${morceaux.join(', ')}.`
          : `Encaissement enregistré (${mode}).`
      );
      setEncaissementTarget(null);
      setEncaissementDetail(null);
      setUsageIdsCoches([]);
      setDroitCoche(false);
      setMontantCheque('');
      setMontantEdite(false);
      setConfirmerAvoir(false);
      await fetchUsagers({ silent: true });
    } catch (err) {
      const msg = err.response?.data?.message || "Erreur lors de l'encaissement";
      setEncaissementError(msg);
      toast.error(msg);
    } finally {
      setEncaissementSubmitting(false);
    }
  };

  const hasActiveFilters = Boolean(
    search.trim() ||
      filterStatut ||
      filterDroitUsage ||
      filterCaution ||
      annee !== currentYear
  );

  const resetFilters = () => {
    setSearch('');
    setFilterStatut('');
    setFilterDroitUsage('');
    setFilterCaution('');
    setAnnee(currentYear);
    setOpenFilter('');
  };

  const toggleFilter = (id) => {
    setOpenFilter((prev) => (prev === id ? '' : id));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return usagers.filter((u) => {
      if (q) {
        const hay = `${u.user?.prenom || ''} ${u.user?.nom || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterStatut && u.status !== filterStatut) return false;
      if (filterDroitUsage === 'oui' && !u.droitUsageRegle) return false;
      if (filterDroitUsage === 'non' && u.droitUsageRegle) return false;
      if (filterCaution === 'oui' && !u.cautionReglee) return false;
      if (filterCaution === 'non' && u.cautionReglee) return false;
      return true;
    });
  }, [usagers, search, filterStatut, filterDroitUsage, filterCaution]);

  const CheckCell = ({ ok, titleOk, titleKo }) =>
    ok ? (
      <span
        className="inline-flex items-center justify-center text-green-600"
        title={titleOk}
      >
        <CheckCircleIcon className="h-5 w-5" />
      </span>
    ) : (
      <span
        className="inline-flex items-center justify-center text-red-500"
        title={titleKo}
      >
        <XCircleIcon className="h-5 w-5" />
      </span>
    );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium text-sm">Erreur</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button type="button" onClick={() => setError('')} className="text-red-600">
            ×
          </button>
        </div>
      )}

      <div className="space-y-3 pb-3 border-b border-slate-100">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Usagers miellerie ({filtered.length}
            {hasActiveFilters && filtered.length !== usagers.length
              ? ` / ${usagers.length}`
              : ''}
            )
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Droit d&apos;usage, caution, dette et réservations à venir — année {annee}.
            Les personnes avec une dette (ou un usage) sans souscription miellerie
            apparaissent aussi.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <FilterButton
            label="Statut"
            badge={filterStatut ? statusServiceLabel(filterStatut) : null}
            open={openFilter === 'statut'}
            onToggle={() => toggleFilter('statut')}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-900">Statut</span>
                {filterStatut ? (
                  <button
                    type="button"
                    onClick={() => setFilterStatut('')}
                    className="text-sm text-honey-700 hover:text-honey-800"
                  >
                    Effacer
                  </button>
                ) : null}
              </div>
              <div className="space-y-2">
                {STATUT_FILTER_OPTIONS.filter((opt) => opt.value).map((opt) => (
                  <FilterOption
                    key={opt.value}
                    selected={filterStatut === opt.value}
                    onClick={() => {
                      setFilterStatut(opt.value);
                      setOpenFilter('');
                    }}
                  >
                    {opt.label}
                  </FilterOption>
                ))}
              </div>
            </div>
          </FilterButton>

          <FilterButton
            label="Droit d'usage"
            badge={
              filterDroitUsage
                ? DROIT_USAGE_OPTIONS.find((o) => o.value === filterDroitUsage)?.label
                : null
            }
            open={openFilter === 'droit'}
            onToggle={() => toggleFilter('droit')}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-900">Droit d&apos;usage</span>
                {filterDroitUsage ? (
                  <button
                    type="button"
                    onClick={() => setFilterDroitUsage('')}
                    className="text-sm text-honey-700 hover:text-honey-800"
                  >
                    Effacer
                  </button>
                ) : null}
              </div>
              <div className="space-y-2">
                {DROIT_USAGE_OPTIONS.filter((opt) => opt.value).map((opt) => (
                  <FilterOption
                    key={opt.value}
                    selected={filterDroitUsage === opt.value}
                    onClick={() => {
                      setFilterDroitUsage(opt.value);
                      setOpenFilter('');
                    }}
                  >
                    {opt.label}
                  </FilterOption>
                ))}
              </div>
            </div>
          </FilterButton>

          <FilterButton
            label="Caution"
            badge={
              filterCaution
                ? CAUTION_OPTIONS.find((o) => o.value === filterCaution)?.label
                : null
            }
            open={openFilter === 'caution'}
            onToggle={() => toggleFilter('caution')}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-900">Caution</span>
                {filterCaution ? (
                  <button
                    type="button"
                    onClick={() => setFilterCaution('')}
                    className="text-sm text-honey-700 hover:text-honey-800"
                  >
                    Effacer
                  </button>
                ) : null}
              </div>
              <div className="space-y-2">
                {CAUTION_OPTIONS.filter((opt) => opt.value).map((opt) => (
                  <FilterOption
                    key={opt.value}
                    selected={filterCaution === opt.value}
                    onClick={() => {
                      setFilterCaution(opt.value);
                      setOpenFilter('');
                    }}
                  >
                    {opt.label}
                  </FilterOption>
                ))}
              </div>
            </div>
          </FilterButton>

          <FilterButton
            label="Année"
            badge={String(annee)}
            open={openFilter === 'annee'}
            onToggle={() => toggleFilter('annee')}
            menuWidth="w-48"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-900">Année</span>
              </div>
              <div className="space-y-2">
                {anneesOptions.map((y) => (
                  <FilterOption
                    key={y}
                    selected={annee === y}
                    onClick={() => {
                      setAnnee(y);
                      setOpenFilter('');
                    }}
                  >
                    {y}
                  </FilterOption>
                ))}
              </div>
            </div>
          </FilterButton>

          <div className="flex-1 min-w-[12rem] max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, prénom"
                aria-label="Filtrer par nom ou prénom"
                className="block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md leading-5 bg-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Réinitialiser les filtres
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="p-12 text-center">
          <UsersIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            {usagers.length === 0
              ? `Aucun usager miellerie pour ${annee}`
              : 'Aucun résultat pour ces filtres'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usager
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Contact
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                  Statut
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Droit d&apos;usage
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Caution
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dette
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rés. à venir
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rés. passées
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((u) => (
                <tr
                  key={String(u.serviceId || u.user?._id)}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-honey-100 flex items-center justify-center shrink-0">
                        <span className="text-honey-800 text-xs font-semibold">
                          {u.user.prenom?.[0]}
                          {u.user.nom?.[0]}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {u.user.prenom} {u.user.nom}
                        </p>
                        {u.sansSouscription ? (
                          <p className="text-xs text-red-700">Pas de souscription {annee}</p>
                        ) : (
                          <p className="text-xs text-slate-500 md:hidden truncate">
                            {u.user.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-sm text-slate-900">{u.user.email}</p>
                    <p className="text-xs text-slate-500">
                      {u.user.telephone || '—'}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-center hidden lg:table-cell">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(u.status)}`}
                    >
                      {statusServiceLabel(u.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.sansSouscription ? (
                      <span className="text-sm text-slate-400" title="Pas de souscription miellerie">
                        —
                      </span>
                    ) : u.droitUsageRegle ? (
                      <CheckCell
                        ok
                        titleOk="Droit d'usage réglé"
                        titleKo="Droit d'usage non réglé"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <CheckCell
                          ok={false}
                          titleOk="Droit d'usage réglé"
                          titleKo="Droit d'usage non réglé"
                        />
                        {canManageActivite && !(u.dette > 0) ? (
                          <button
                            type="button"
                            onClick={() => openEncaissementModal(u)}
                            className="inline-flex items-center justify-center text-xs font-semibold text-honey-800 bg-honey-50 hover:bg-honey-100 border border-honey-200 px-2 py-1 rounded-md whitespace-nowrap"
                          >
                            Encaisser
                          </button>
                        ) : null}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.sansSouscription ? (
                      <span className="text-sm text-slate-400" title="Pas de souscription miellerie">
                        —
                      </span>
                    ) : u.cautionReglee ? (
                      <CheckCell
                        ok
                        titleOk={`Caution : ${u.cautionStatus}`}
                        titleKo="Caution non reçue"
                      />
                    ) : canManageActivite ? (
                      <button
                        type="button"
                        onClick={() => openCautionModal(u)}
                        className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-honey-800 bg-honey-50 hover:bg-honey-100 border border-honey-200 px-2 py-1 rounded-md whitespace-nowrap"
                      >
                        Marquer reçue
                      </button>
                    ) : (
                      <CheckCell
                        ok={false}
                        titleOk={`Caution : ${u.cautionStatus}`}
                        titleKo="Caution non reçue"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {Number(u.solde) < 0 ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-red-700 tabular-nums">
                          – {u.dette.toFixed(2)} €
                        </span>
                        {canManageActivite ? (
                          <button
                            type="button"
                            onClick={() => openEncaissementModal(u)}
                            className="inline-flex items-center justify-center text-xs font-semibold text-honey-800 bg-honey-50 hover:bg-honey-100 border border-honey-200 px-2 py-1 rounded-md whitespace-nowrap"
                          >
                            Encaisser
                          </button>
                        ) : null}
                      </div>
                    ) : Number(u.solde) > 0 ? (
                      <span
                        className="text-sm font-semibold text-green-700 tabular-nums"
                        title="Avoir"
                      >
                        + {Number(u.solde).toFixed(2)} €
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex min-w-[1.75rem] justify-center px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${
                        u.nbReservationsAVenir > 0
                          ? 'bg-honey-100 text-honey-900'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {u.nbReservationsAVenir}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.nbReservationsPassees > 0 ? (
                      <button
                        type="button"
                        onClick={() => openReservationsPassees(u)}
                        className="inline-flex min-w-[1.75rem] justify-center px-2 py-0.5 rounded-full text-xs font-bold tabular-nums bg-slate-800 text-white hover:bg-slate-700"
                        title="Voir les réservations passées"
                      >
                        {u.nbReservationsPassees}
                      </button>
                    ) : (
                      <span className="inline-flex min-w-[1.75rem] justify-center px-2 py-0.5 rounded-full text-xs font-bold tabular-nums bg-slate-100 text-slate-500">
                        0
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cautionTarget && (
        <Portal>
          <div
            className={OVERLAY_CLASS}
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeCautionModal();
            }}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col"
              role="dialog"
              aria-labelledby="caution-recue-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
                <div>
                  <h3 id="caution-recue-title" className="text-base font-semibold text-slate-900">
                    Marquer la caution comme reçue
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {cautionTarget.user
                      ? `${cautionTarget.user.prenom || ''} ${cautionTarget.user.nom || ''}`.trim()
                      : '—'}
                    <span className="ml-1.5 text-honey-800 font-medium">
                      ({cautionTarget.cautionMontant || 300} €)
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeCautionModal}
                  disabled={cautionSubmitting}
                  className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 shrink-0"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {cautionError && (
                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {cautionError}
                  </p>
                )}

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Date de réception
                  </label>
                  <input
                    type="date"
                    value={cautionForm.dateReception}
                    onChange={(e) =>
                      setCautionForm((p) => ({ ...p, dateReception: e.target.value }))
                    }
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-honey-400 focus:border-honey-400 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Note (optionnelle)
                  </label>
                  <input
                    type="text"
                    value={cautionForm.note}
                    onChange={(e) => setCautionForm((p) => ({ ...p, note: e.target.value }))}
                    placeholder="Ex. chèque n° 123456…"
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-honey-400 focus:border-honey-400 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Document justificatif (optionnel)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setCautionForm((p) => ({ ...p, document: e.target.files[0] || null }))
                    }
                    className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-2.5 file:rounded file:border-0 file:text-xs file:font-medium file:bg-honey-100 file:text-honey-800 hover:file:bg-honey-200"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={closeCautionModal}
                  disabled={cautionSubmitting}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleMarkCautionRecu}
                  disabled={cautionSubmitting}
                  className="px-4 py-2 bg-honey-600 hover:bg-honey-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {cautionSubmitting ? 'Enregistrement…' : 'Enregistrer la réception'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {encaissementTarget && (
        <Portal>
          <div
            className={OVERLAY_CLASS}
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeEncaissementModal();
            }}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
              role="dialog"
              aria-labelledby="encaissement-dette-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
                <div>
                  <h3 id="encaissement-dette-title" className="text-base font-semibold text-slate-900">
                    Encaisser
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {encaissementTarget.user
                      ? `${encaissementTarget.user.prenom || ''} ${encaissementTarget.user.nom || ''}`.trim()
                      : '—'}
                    {encaissementDetail?.dette > 0 ? (
                      <span className="ml-1.5 text-red-700 font-medium">
                        ({Number(encaissementDetail.dette).toFixed(2)} €)
                      </span>
                    ) : null}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeEncaissementModal}
                  disabled={encaissementSubmitting}
                  className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 shrink-0"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {encaissementError && (
                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {encaissementError}
                  </p>
                )}

                {encaissementLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-honey-600" />
                  </div>
                ) : (
                  <>
                    {encaissementDetail?.accesBloqueDepuis ? (
                      <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        Accès bloqué depuis le{' '}
                        {formatDateUsage(encaissementDetail.accesBloqueDepuis)}. Il sera rétabli
                        si cet encaissement solde la dette.
                      </p>
                    ) : null}

                    <div>
                      <p className="text-xs font-medium text-slate-700 mb-2">Lignes à encaisser</p>
                      {(() => {
                        const usages = encaissementDetail?.usages || [];
                        const droit = encaissementDetail?.droitUsage;
                        if (!encaissementDetail) {
                          return (
                            <p className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                              Le détail n&apos;a pas pu être chargé.
                            </p>
                          );
                        }
                        if (!usages.length && !droit) {
                          return (
                            <p className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                              Rien à encaisser pour cet usager.
                            </p>
                          );
                        }
                        return (
                          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                            {usages.map((usage) => {
                              const id = String(usage._id);
                              const coche = usageIdsCoches.includes(id);
                              const ligne = repartition.lignes.find((l) => String(l._id) === id);
                              return (
                                <li key={id}>
                                  <label className="px-3 py-2 flex items-start gap-3 cursor-pointer hover:bg-slate-50">
                                    <input
                                      type="checkbox"
                                      checked={coche}
                                      onChange={() => toggleUsageCoche(usage)}
                                      disabled={encaissementSubmitting}
                                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-honey-600 focus:ring-honey-500"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm text-slate-900">{usage.libelle}</p>
                                      <p className="text-xs text-slate-500">
                                        {formatDateUsage(usage.date)}
                                        {usage.annee ? ` · ${usage.annee}` : ''}
                                        {' · en dette'}
                                      </p>
                                      {coche && ligne?.issue === 'paye' ? (
                                        <p className="text-xs text-green-700 mt-0.5">Réglé en entier</p>
                                      ) : null}
                                      {coche && ligne?.issue === 'partiel' ? (
                                        <p className="text-xs text-honey-800 mt-0.5">
                                          Partiel : {ligne.encaisse.toFixed(2)} € encaissés, reste {ligne.restant.toFixed(2)} € en dette
                                        </p>
                                      ) : null}
                                      {coche && ligne?.issue === 'non_atteint' ? (
                                        <p className="text-xs text-red-700 mt-0.5">
                                          Non couvert — décochez cette ligne
                                        </p>
                                      ) : null}
                                    </div>
                                    <span className="text-sm font-medium text-slate-800 tabular-nums shrink-0">
                                      {Number(usage.montant || 0).toFixed(2)} €
                                    </span>
                                  </label>
                                </li>
                              );
                            })}
                            {droit ? (
                              <li>
                                <label className="px-3 py-2 flex items-start gap-3 cursor-pointer hover:bg-honey-50 bg-honey-50/50">
                                  <input
                                    type="checkbox"
                                    checked={droitCoche}
                                    onChange={toggleDroitCoche}
                                    disabled={encaissementSubmitting}
                                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-honey-600 focus:ring-honey-500"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm text-slate-900">{droit.libelle}</p>
                                    <p className="text-xs text-slate-500">Non réglé</p>
                                    {droitCoche && repartition.droitIssue === 'paye' ? (
                                      <p className="text-xs text-green-700 mt-0.5">Réglé en entier</p>
                                    ) : null}
                                    {droitCoche && (repartition.droitIssue === 'insuffisant' || repartition.droitIssue === 'non_atteint') ? (
                                      <p className="text-xs text-red-700 mt-0.5">
                                        Non couvert en entier — décochez-le ou augmentez le montant
                                      </p>
                                    ) : null}
                                  </div>
                                  <span className="text-sm font-medium text-slate-800 tabular-nums shrink-0">
                                    {Number(droit.montant || 0).toFixed(2)} €
                                  </span>
                                </label>
                              </li>
                            ) : null}
                          </ul>
                        );
                      })()}
                      {(() => {
                        const usages = encaissementDetail?.usages || [];
                        const dette = Number(encaissementDetail?.dette || 0);
                        if (!usages.length || !(dette > 0)) return null;
                        const somme = roundEuro(
                          usages.reduce((s, u) => s + Number(u.montant || 0), 0)
                        );
                        if (Math.abs(somme - dette) <= 0.02) return null;
                        return (
                          <p className="text-xs text-slate-600 mt-2">
                            Le solde dû est de {dette.toFixed(2)} €, la somme des usages en dette est de{' '}
                            {somme.toFixed(2)} €. Le compte est crédité du montant des usages cochés,
                            dans la limite du solde dû.
                          </p>
                        );
                      })()}
                    </div>

                    <div>
                      <label htmlFor="encaissement-montant" className="block text-xs font-medium text-slate-700 mb-1">
                        Montant encaissé
                      </label>
                      <input
                        id="encaissement-montant"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={montantCheque}
                        onChange={(e) => {
                          setMontantEdite(true);
                          setMontantCheque(e.target.value);
                        }}
                        disabled={encaissementSubmitting}
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-honey-400 focus:border-honey-400 bg-white"
                      />
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <p className="text-xs text-slate-500">
                          Somme des lignes cochées : {montantEncaissement.toFixed(2)} €.
                          Le montant est réparti du plus ancien au plus récent.
                        </p>
                        {montantEdite ? (
                          <button
                            type="button"
                            onClick={() => {
                              setMontantEdite(false);
                              setMontantCheque(
                                montantEncaissement > 0 ? montantEncaissement.toFixed(2) : ''
                              );
                            }}
                            disabled={encaissementSubmitting}
                            className="shrink-0 text-xs font-medium text-honey-800 hover:text-honey-900"
                          >
                            Reprendre la somme
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-700">Mode de règlement</p>
                      {[
                        { value: 'cheque', label: 'Chèque' },
                        { value: 'espece', label: 'Espèces' },
                      ].map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name="encaissement-methode"
                            checked={encaissementForm.methode === value}
                            onChange={() =>
                              setEncaissementForm((p) => ({ ...p, methode: value }))
                            }
                            disabled={encaissementSubmitting}
                            className="text-honey-600"
                          />
                          {label}
                        </label>
                      ))}
                    </div>

                    {encaissementForm.methode === 'cheque' && (
                      <div>
                        <label htmlFor="encaissement-ref" className="block text-xs font-medium text-slate-700 mb-1">
                          Nº chèque (optionnel)
                        </label>
                        <input
                          id="encaissement-ref"
                          type="text"
                          value={encaissementForm.reference}
                          onChange={(e) =>
                            setEncaissementForm((p) => ({ ...p, reference: e.target.value }))
                          }
                          disabled={encaissementSubmitting}
                          className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-honey-400 focus:border-honey-400 bg-white"
                        />
                      </div>
                    )}

                    <div>
                      <label htmlFor="encaissement-note" className="block text-xs font-medium text-slate-700 mb-1">
                        Note (optionnelle)
                      </label>
                      <input
                        id="encaissement-note"
                        type="text"
                        value={encaissementForm.note}
                        onChange={(e) =>
                          setEncaissementForm((p) => ({ ...p, note: e.target.value }))
                        }
                        disabled={encaissementSubmitting}
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-honey-400 focus:border-honey-400 bg-white"
                      />
                    </div>

                    {repartition.avoir > 0 ? (
                      <label className="flex items-start gap-2 text-sm text-slate-800 bg-honey-50 border border-honey-200 rounded-lg px-3 py-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmerAvoir}
                          onChange={(e) => setConfirmerAvoir(e.target.checked)}
                          disabled={encaissementSubmitting}
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-honey-600 focus:ring-honey-500"
                        />
                        <span>
                          Le surplus de {repartition.avoir.toFixed(2)} € sera crédité en avoir
                          sur le compte, utilisable à la prochaine réservation.
                        </span>
                      </label>
                    ) : null}
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={closeEncaissementModal}
                  disabled={encaissementSubmitting}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleEncaisserDette}
                  disabled={
                    encaissementSubmitting ||
                    encaissementLoading ||
                    (!usageIdsCoches.length && !(droitCoche && encaissementDetail?.droitUsage)) ||
                    !(parseMontant(montantCheque) > 0) ||
                    repartition.lignes.some((l) => l.issue === 'non_atteint') ||
                    (droitCoche &&
                      (repartition.droitIssue === 'insuffisant' ||
                        repartition.droitIssue === 'non_atteint')) ||
                    (repartition.avoir > 0 && !confirmerAvoir)
                  }
                  className="px-4 py-2 bg-honey-600 hover:bg-honey-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {encaissementSubmitting ? 'Enregistrement…' : 'Enregistrer l’encaissement'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {passeesTarget && (
        <Portal>
          <div
            className={OVERLAY_CLASS}
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeReservationsPassees();
            }}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
              role="dialog"
              aria-labelledby="passees-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
                <div>
                  <h3 id="passees-title" className="text-base font-semibold text-slate-900">
                    Réservations passées
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {`${passeesTarget.user?.prenom || ''} ${passeesTarget.user?.nom || ''}`.trim() || '—'}
                    {' · '}
                    {annee}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeReservationsPassees}
                  className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 shrink-0"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div ref={passeesListRef} className="flex-1 overflow-y-auto p-6 space-y-2">
                {passeesError ? (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                    {passeesError}
                  </div>
                ) : null}
                {passeesLoading ? (
                  <p className="text-sm text-slate-500">Chargement…</p>
                ) : null}
                {!passeesLoading && !passeesError && passeesListe.length === 0 ? (
                  <p className="text-sm text-slate-500">Aucune réservation passée.</p>
                ) : null}
                {passeesVisibles.map((r) => (
                  <div
                    key={String(r._id)}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm flex items-center justify-between gap-3"
                  >
                    <div className="flex flex-col items-start justify-between">
                      <div className="flex items-start justify-between gap-3 w-3/4">
                        <p className="text-slate-600 text-xs">
                          {formatDateUsage(r.date)}
                          {r.heureDebut ? (
                            <span className="font-normal text-slate-600">
                              {' · '}
                              {r.heureDebut}
                              {r.heureFin ? `–${r.heureFin}` : ''}
                            </span>
                          ) : null}
                        </p>
                      </div>
                      <p className="text-slate-900 text-[15px] mt-0.5 font-medium">{libellePrestationPassee(r.prestation)}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${classeStatutPassee(r.statut)}`}
                        >
                          {libelleStatutPassee(r)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center gap-1.5 w-1/4">
                        <span className={`text-[16px] font-semibold tabular-nums text-slate-800 shrink-0 ${Number(r.montantTotal) === 0 ? 'text-slate-300' : ''}`}>
                          {Number(r.montantTotal).toFixed(2)} €
                        </span>
                        {badgesPaiementPassee(r).map((badge) => (
                          <span
                            key={badge.label}
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border border-slate-200`}
                          >
                            {badge.label}
                          </span>
                        ))}
                      </div>
                  </div>
                ))}
              </div>
              {!passeesLoading && !passeesError && passeesListe.length > PASSEES_PAR_PAGE ? (
                <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={() => setPasseesPage((p) => Math.max(0, p - 1))}
                    disabled={passeesPageCourante === 0}
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
                  >
                    Précédent
                  </button>
                  <span className="text-sm text-slate-500 tabular-nums">
                    {passeesPageCourante * PASSEES_PAR_PAGE + 1}
                    –
                    {Math.min((passeesPageCourante + 1) * PASSEES_PAR_PAGE, passeesListe.length)}
                    {' sur '}
                    {passeesListe.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPasseesPage((p) => Math.min(passeesTotalPages - 1, p + 1))}
                    disabled={passeesPageCourante >= passeesTotalPages - 1}
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
                  >
                    Suivant
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
